import {
  Autocomplete,
  AutocompleteRenderInputParams,
  TextField,
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getPlaceDetails } from '../../../common/utils/googlePlaces';
import { useDoctorsSearch } from '../hooks';
import { Doctor } from '../types';
import './DoctorsSearch.scss';

const DoctorsSearch = ({
  applicationId,
  taskId,
  onSelect,
}: {
  applicationId: number;
  taskId: number;
  onSelect: (doctor: Doctor | null) => void;
}) => {
  const [query, setQuery] = useState('');
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
  const isMountedRef = useRef(true);

  const { doctors, isLoading } = useDoctorsSearch(
    applicationId,
    taskId,
    query,
    query.length > 0,
  );

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleDoctorSelect = useCallback(
    async (_event: React.SyntheticEvent, value: Doctor | null) => {
      if (!value) {
        onSelect(null);
        return;
      }

      if (value.source === 'google_places' && value.placeId) {
        if (!isMountedRef.current) {
          return;
        }

        setIsFetchingDetails(true);

        try {
          const placeDetails = await getPlaceDetails(value.placeId);

          if (!isMountedRef.current) {
            return;
          }

          const enhancedDoctor: Doctor = {
            ...value,
            name: placeDetails.name || value.name,
            phoneNumber: placeDetails.phoneNumber || value.phoneNumber,
            address:
              placeDetails.addressComponents?.street1 || value.address || '',
            city: placeDetails.addressComponents?.locality || value.city || '',
            state: placeDetails.addressComponents?.region || value.state || '',
            zipCode:
              placeDetails.addressComponents?.postalCode || value.zipCode || '',
            information:
              placeDetails.formattedAddress || value.information || '',
          };

          if (placeDetails.addressComponents) {
            const addrParts = [
              placeDetails.addressComponents.street1,
              placeDetails.addressComponents.street2,
            ]
              .filter(Boolean)
              .join(', ');
            if (addrParts) {
              enhancedDoctor.address = addrParts;
            }
          }

          onSelect(enhancedDoctor);
        } catch (error) {
          if (isMountedRef.current) {
            console.error('Failed to fetch place details:', error);
            onSelect(value);
          }
        } finally {
          if (isMountedRef.current) {
            setIsFetchingDetails(false);
          }
        }
      } else {
        onSelect(value);
      }
    },
    [onSelect],
  );

  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <TextField
        {...params}
        fullWidth
        placeholder="Search hospital, clinic or physician name…"
        variant="outlined"
        size="small"
      />
    ),
    [],
  );

  const renderOption = useCallback(
    (props: React.HTMLAttributes<HTMLLIElement>, option: Doctor) => (
      <li {...props} key={option.id} className="google-result-option">
        <span className="doctor-checkbox" aria-hidden />
        <div className="doctor-main">
          <div className="doctor-name">{option.name || '-'}</div>
          {option.information && (
            <div className="doctor-meta">
              <span className="info-pill">{option.information}</span>
            </div>
          )}
        </div>
      </li>
    ),
    [],
  );

  return (
    <div className="others-panel">
      <div className="google-badge">
        <span className="google-dot" />
        Searching via Google Medical API
      </div>

      <div className="google-search-bar">
        <Autocomplete
          fullWidth
          options={doctors}
          loading={isLoading || isFetchingDetails}
          getOptionLabel={option => option.name || ''}
          isOptionEqualToValue={(option, value) => {
            if (option.placeId && value.placeId) {
              return option.placeId === value.placeId;
            }
            if (option.doctorUuid && value.doctorUuid) {
              return option.doctorUuid === value.doctorUuid;
            }
            if (option.id && value.id) {
              return option.id === value.id;
            }
            return false;
          }}
          renderOption={renderOption}
          disablePortal
          filterOptions={options => options}
          onInputChange={(_, value) => setQuery(value)}
          onChange={handleDoctorSelect}
          renderInput={renderInput}
          noOptionsText="No doctors found"
        />
      </div>

      {query.length === 0 && (
        <div className="google-placeholder">
          Type a hospital, clinic or physician name to search
        </div>
      )}
    </div>
  );
};

export default DoctorsSearch;
